import {useLayoutEffect, useRef} from 'react'
import _ from 'lodash'

const getNativeInput = node => {
	return node?.input || node?.resizableTextArea?.textArea || node?.nativeElement?.querySelector?.('input, textarea') || node
}

export const usePreserveInputSelection = ({innerRef, value, remapSelection}) => {
	const inputRef = useRef(null)
	const pendingSelectionRef = useRef(null)

	const setRefs = node => {
		inputRef.current = node

		if (!innerRef) return
		if (_.isFunction(innerRef)) {
			innerRef(node)
			return
		}

		innerRef.current = node
	}

	useLayoutEffect(() => {
		const selection = pendingSelectionRef.current
		if (!selection) return

		pendingSelectionRef.current = null
		const inputNode = getNativeInput(inputRef.current)
		if (!inputNode || typeof inputNode.setSelectionRange !== 'function') return
		if (typeof document !== 'undefined' && document.activeElement !== inputNode) return

		try {
			const nextSelection = remapSelection?.({selection, value}) || selection
			// React + AntD can move the caret during controlled rerenders.
			// Restoring the selection here lets us keep the input controlled without
			// bringing back the local-value buffer that triggered the earlier loop.
			inputNode.setSelectionRange(nextSelection.start, nextSelection.end, nextSelection.direction)
		} catch {
			// Some input types do not support selection ranges.
		}
	}, [value])

	const storeSelection = event => {
		pendingSelectionRef.current = {
			start: event.target.selectionStart ?? 0,
			end: event.target.selectionEnd ?? 0,
			direction: event.target.selectionDirection,
			value: event.target.value,
		}
	}

	const clearSelection = () => {
		pendingSelectionRef.current = null
	}

	return {setRefs, storeSelection, clearSelection}
}
